/**
 * The usage-limit block, as ONE typed value from the gateway to the banner (MAR-1009).
 *
 * `services/llm-gateway` is the only thing that decides quota (#769). When it refuses a model call it
 * answers 429 with a stable body — `{code: "usage_limit_reached", category, plan, plan_label, reason}`
 * (`proxy.rs`'s `quota_exceeded`). That fact then crosses several hops before a person sees it:
 *
 *   gateway 429 ─▶ agent service / docproc / web's embedding client
 *               ─▶ web (A2A outcome, A2A task metadata, docproc callback)
 *               ─▶ web's answer to the browser: 429 `USAGE_LIMIT_REACHED` (or, on a stream that is
 *                  already open, a `usage_limit` event carrying the same body)
 *               ─▶ `useUsageLimit` → the red banner naming the plan, and an Activity item.
 *
 * At EVERY hop it travels as this structure, never as prose. Before this file the hops were text:
 * chat regex-parsed `run stopped: exhausted` out of a failed task, and nothing anywhere sent the
 * `USAGE_LIMIT_REACHED` the banner listens for, so a real block never showed it.
 *
 * Two spellings of the same value, deliberately: the gateway's and the agents' wire body is
 * snake_case (`plan_label`), what web sends its own client is camelCase (`planLabel`) like every other
 * web response. The two parsers below are the only place either is read.
 */
import type { UsageCategory } from './surfaces'

/** What `web` answers its client with — `server/utils/orgPlan.ts` re-exports it as `USAGE_LIMIT_CODE`. */
export const USAGE_LIMIT_REACHED = 'USAGE_LIMIT_REACHED' as const

/** The gateway's `code` (and every agent service's typed payload). Must match `proxy.rs`'s `USAGE_LIMIT_CODE`. */
export const GATEWAY_USAGE_LIMIT_CODE = 'usage_limit_reached' as const

/**
 * `exceeds_plan` — the category's limit on this plan is zero, so waiting never helps; `exhausted` — a
 * nonzero allowance ran out this month and resets next month. Same two words as `quotaBlock.ts`.
 */
export type UsageLimitReason = 'exhausted' | 'exceeds_plan'

export interface UsageLimitBlock {
  category: UsageCategory | null
  /** The plan id (`plans.id`). */
  plan: string | null
  /** The plan's human label (`plans.label`) — what the banner names. */
  planLabel: string | null
  reason: UsageLimitReason
  /**
   * The user whose seat hit it, where web stores a block on an object others share (a sheet cell;
   * MAR-1046 AC17) — only they are shown the plan. Absent on a live answer, which is always the
   * requester's own.
   */
  userId?: string | null
}

/** The body of web's 429, and of the chat stream's `usage_limit` event. */
export interface UsageLimitResponse extends UsageLimitBlock {
  code: typeof USAGE_LIMIT_REACHED
}

// `gemma` included (MAR-1211): the gateway refuses a `free_tier` member's landing-research call
// with `category: 'gemma'`, and dropping it to `null` would lose the one fact that says which line to show.
const CATEGORIES: ReadonlySet<string> = new Set<UsageCategory>(['chat', 'sheet', 'workflow', 'read', 'deep-read', 'pro-read', 'gemma'])
const REASONS: ReadonlySet<string> = new Set<UsageLimitReason>(['exhausted', 'exceeds_plan'])

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function category(value: unknown): UsageCategory | null {
  return typeof value === 'string' && CATEGORIES.has(value) ? value as UsageCategory : null
}

/**
 * The gateway's (or an agent's) snake_case body → a block, or `null` when it is not a usage-limit
 * block. A run-ceiling 429 is NOT one — it carries no `code` and is about one runaway turn, not the
 * organization's plan — and neither is a provider's own rate limit.
 */
export function parseGatewayUsageLimit(body: unknown): UsageLimitBlock | null {
  if (!body || typeof body !== 'object') return null
  const raw = body as Record<string, unknown>
  if (raw.code !== GATEWAY_USAGE_LIMIT_CODE) return null
  return {
    category: category(raw.category),
    plan: text(raw.plan),
    planLabel: text(raw.plan_label) ?? text(raw.planLabel),
    reason: typeof raw.reason === 'string' && REASONS.has(raw.reason) ? raw.reason as UsageLimitReason : 'exhausted',
  }
}

/**
 * The i18n key that says `block` to its user. A `gemma` block is the free tier's (MAR-1211): only an
 * `free_tier` member's landing research is gated on `gemma`, and its allowance is the month's
 * free research, so it reads "Usaste tu investigación gratuita de este mes", not a plan limit
 * (`service.usageLimitReached`, which takes `{plan}`).
 */
export function usageLimitMessageKey(block: Pick<UsageLimitBlock, 'category'>): 'service.freeResearchUsed' | 'service.usageLimitReached' {
  return block.category === 'gemma' ? 'service.freeResearchUsed' : 'service.usageLimitReached'
}

export function toUsageLimitResponse(block: UsageLimitBlock): UsageLimitResponse {
  return { code: USAGE_LIMIT_REACHED, ...block }
}

/** Web's own camelCase body → a response, or `null` when it is not one. What the client reads. */
export function parseUsageLimitResponse(data: unknown): UsageLimitResponse | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  if (raw.code !== USAGE_LIMIT_REACHED) return null
  return {
    code: USAGE_LIMIT_REACHED,
    category: category(raw.category),
    plan: text(raw.plan),
    planLabel: text(raw.planLabel),
    reason: typeof raw.reason === 'string' && REASONS.has(raw.reason) ? raw.reason as UsageLimitReason : 'exhausted',
    ...(text(raw.userId) ? { userId: text(raw.userId) } : {}),
  }
}

/**
 * The block as the ONE string a text column can hold — `sheet_cells.error_message`.
 *
 * A cell the gateway refused used to store `UsageLimitReachedError`'s English diagnostic, and the grid
 * printed it as the cell's tooltip ("usage limit reached: sheet on plan release-test-0
 * (exceeds_plan)", the 10-02 dev re-test of MAR-1009). The cell has no other column to carry a
 * structure in, so the block is stored encoded — the move `quotaBlock.ts` makes for
 * `items.error_message` — and the page parses it back and says it in the reader's language. Nothing
 * renders the stored form.
 */
const MESSAGE_PREFIX = 'usage_limit:v1:'

export function encodeUsageLimitMessage(block: UsageLimitBlock): string {
  const { category, plan, planLabel, reason, userId } = block
  return MESSAGE_PREFIX + JSON.stringify({ category, plan, planLabel, reason, ...(userId ? { userId } : {}) })
}

/** A stored message → the block, or `null` when it is any other message (or none). */
export function parseUsageLimitMessage(message: string | null | undefined): UsageLimitResponse | null {
  if (!message || !message.startsWith(MESSAGE_PREFIX)) return null
  let raw: unknown
  try {
    raw = JSON.parse(message.slice(MESSAGE_PREFIX.length))
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object') return null
  return parseUsageLimitResponse({ ...raw, code: USAGE_LIMIT_REACHED })
}
